import { ClientPlatformType } from '@ps/platform-message-framework/aux/ClientPlatformType';
import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { OSId } from '@ps/platform-message-framework/common/OSId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import type { Selector } from 'reselect';

import { select } from '@swf/core/store';
import type { ModelConfig, RootState, SelectorCreator, Slicer } from '@swf/core/store';

import acceptRacesTerms from './acceptRacesTerms';
import fetchAppData from './fetchAppData';
import fetchFirstFsb from './fetchFirstFsb';
import fetchPlayerState from './fetchPlayerState';
import gameSessionCheck from './gameSessionCheck';
import launch from './launch';
import open from './open';
import play from './play';
import race from './race';
import reconnect from './reconnect';
import toggleFavoriteGame from './toggleFavoriteGame';

export interface CasinoState {
  launchCount: number;
  UKTermsAccepted: boolean;
  liveRacesTermsAccepted: boolean;
  racesTermsVersionAccepted: number;
}

const reducers = {
  addLaunch: (state: CasinoState): CasinoState => ({
    ...state,
    launchCount: state.launchCount + 1
  }),
  set: (state: CasinoState, payload: Partial<CasinoState>): CasinoState => ({
    ...state,
    ...payload
  })
};

const effects = {
  acceptRacesTerms,
  fetchAppData,
  fetchFirstFsb,
  fetchPlayerState,
  gameSessionCheck,
  launch,
  open,
  play,
  race,
  reconnect,
  toggleFavoriteGame
};

export const selectors = (slice: Slicer<CasinoState>, createSelector: SelectorCreator) => ({
  getLaunchCount() {
    return slice((state) => state.launchCount);
  },
  UKTermsAccepted() {
    return slice((state) => state.UKTermsAccepted);
  },
  liveRacesTermsAccepted() {
    return slice((state) => state.liveRacesTermsAccepted);
  },
  racesTermsVersionAccepted() {
    return slice((state) => state.racesTermsVersionAccepted);
  },
  acceptedRacesTermsAreCurrent(this: { racesTermsVersionAccepted: Selector<RootState, number> }) {
    return createSelector(
      slice,
      this.racesTermsVersionAccepted,
      (state: RootState, latestTerms: number) => latestTerms,
      (state, accepted, latestTerms) => accepted === latestTerms
    );
  },
  raceTermsAcceptanceRequired() {
    return createSelector(
      slice,
      (state: RootState, isLiveRace: boolean) => isLiveRace,
      (state: RootState) => select.feature.check(state, FeatureFlag.CasinoSlotRacesTCReqd),
      (state: RootState) => select.feature.check(state, FeatureFlag.CasinoLiveRacesTCReqd),
      (state, isLiveRace, slotFlag: boolean, liveFlag: boolean) =>
        (!isLiveRace && slotFlag) || (isLiveRace && liveFlag)
    );
  },
  stifleUrgencyRequired() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (state, siteId) => [Sites.UK].includes(siteId)
    );
  },
  getHostId() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      (rootState: RootState) => select.cms.showVegasContent(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (websiteBrandId, showVegasContent, siteId) => {
        if (siteId === Sites.DE_SH || siteId === Sites.DE) return 2;
        if (showVegasContent) return 8;

        const brandTypesToHostId = {
          [BrandTypes.POKER_STARS]: 2,
          [BrandTypes.STARS_CASINO]: 7,
          [BrandTypes.BET_STARS]: 6,
          [BrandTypes.FULL_TILT]: 5,
          [BrandTypes.UNKNOWN]: 2,
          [BrandTypes.POKER_STARS_VEGAS]: 8,
          [BrandTypes.FOX_BET]: 10
        };

        return brandTypesToHostId[websiteBrandId];
      }
    );
  },
  getClientPlatform() {
    return createSelector(
      (rootState: RootState) => select.device.get(rootState),
      (rootState: RootState) => select.device.isMobile(rootState),
      ({ embedded, os }, isMobile) => {
        switch (true) {
          case embedded && os === OSId.WINDOWS:
            return ClientPlatformType.WINDOWS;
          case embedded && os === OSId.MAC:
            return ClientPlatformType.MACHINTOSH;
          case embedded && os === OSId.ANDRO_ID:
            return ClientPlatformType.ANDROID;
          case embedded && os === OSId.I_OS:
            return ClientPlatformType.IOS;
          case isMobile:
            return ClientPlatformType.MOBILEWEB;
          default:
            return ClientPlatformType.WEB;
        }
      }
    );
  },
  hasInfoRoute() {
    return createSelector(
      (rootState: RootState) => select.cms.getAliases(rootState),
      (aliases) => !!aliases?.casinoGameInfo
    );
  }
});

interface CasinoModel extends ModelConfig<CasinoState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const casino: CasinoModel = {
  state: {
    launchCount: 0,
    UKTermsAccepted: false,
    liveRacesTermsAccepted: false,
    racesTermsVersionAccepted: 0
  },
  reducers,
  effects,
  selectors
};

export default casino;
